import React, { useState } from 'react'
import loginBackground from '@/assets/images/login_background.png'
import valiantlytixLogo from '@/assets/images/darkLogo.png'
import { Button, Form, Input } from 'antd'
import { EyeInvisibleOutlined, EyeTwoTone, LockFilled } from '@ant-design/icons'
import { Link, useNavigate, useSearchParams } from 'react-router'
import logo from '@/assets/images/vantageAI.png'
import axios from 'axios';
import { toast } from 'sonner'
import './NewLoginPage.css';
import AuthErrorModal from '@/components/AuthErrorModal';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
	const { VITE_APP_URL: URL } = import.meta.env;

	const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

	const [password,setPassword] = useState("");
	const [confirmPassword,setConfirmPassword] = useState("");

	const [buttonLoading,setButtonLoading] = useState(false);

	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [errorTitle, setErrorTitle] = useState<string>("Password Reset Error");

	const handleForgotPassword = async() =>{
		try{
			if ( !password || !confirmPassword) {
				setErrorTitle("Missing Information");
				setErrorMessage("Please fill all the password fields.");
				return;
			}

			if (password !== confirmPassword) {
				setErrorTitle("Password Mismatch");
				setErrorMessage("The passwords you entered do not match. Please verify and try again.");
				return;
			}

			setButtonLoading(true);
			const response = await axios.post(`${URL}/api/users/reset-password`,{
				"token":String(token), "new_password":password
			})

			setButtonLoading(false);	
			toast.success("Password has been reset successfully")
			navigate("/login")
		}catch(err: any){
			console.log(err);
			setErrorTitle("Password Reset Failed");
			if (err?.response?.data?.message || err?.response?.data?.error) {
				setErrorMessage(err.response.data.message || err.response.data.error);
			} else {
				setErrorMessage("Failed to reset your password. The link may have expired or is invalid.");
			}
		} finally {
			setButtonLoading(false);
		}
	}

  return (
    <div className='flex w-full h-screen bg-[#F8F9FA] font-sans'>
      {/* Left side: Form Panel */}
      <div className='w-full lg:w-1/2 h-full flex flex-col items-center relative px-4 sm:px-8 overflow-y-auto'>
        
        <div className='w-full max-w-[440px] flex flex-col items-center my-auto py-8'>
          {/* Logo at Top */}
          <div className='w-full flex justify-center mb-8'>
            <img src={logo} alt="Vantage AI Logo" className='w-[160px] md:w-[200px] h-auto object-contain' />
          </div>

          {/* Form Container */}
          <div className='w-full bg-white px-6 sm:px-8 py-8 sm:py-10 rounded-2xl shadow-sm border border-gray-100 flex flex-col'>
            <div className='flex flex-col gap-2 mb-6'>
              <div className='flex items-center gap-3'>
                <LockFilled className='text-[#A51C1C] text-xl md:text-2xl' />
                <span className='text-xl md:text-2xl font-semibold tracking-[0.2px] text-gray-900'>Reset Password</span>
              </div>
              <span className='text-gray-500 text-sm'>Please enter your new password</span>
            </div>

            <div className='flex flex-col gap-y-3'>
              <Form.Item label={<span className='text-sm font-medium text-gray-700 mb-0 block'>New password</span>} className='mb-0' layout='vertical'>
                <Input.Password
                  size="large"
                  style={{ backgroundColor: "#F8F9FA", borderColor: "#E5E7EB", height: 42 }}
                  className='light-password rounded-lg hover:border-gray-300 focus:border-[#A91D22]'
                  placeholder='Create a new password'
                  prefix={<svg width="19" height="10" viewBox="0 0 19 10" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M4.75 6.65C4.22222 6.65 3.77361 6.46528 3.40417 6.09583C3.03472 5.72639 2.85 5.27778 2.85 4.75C2.85 4.22222 3.03472 3.77361 3.40417 3.40417C3.77361 3.03472 4.22222 2.85 4.75 2.85C5.27778 2.85 5.72639 3.03472 6.09583 3.40417C6.46528 3.77361 6.65 4.22222 6.65 4.75C6.65 5.27778 6.46528 5.72639 6.09583 6.09583C5.72639 6.46528 5.27778 6.65 4.75 6.65ZM4.75 9.5C3.43056 9.5 2.30903 9.03819 1.38542 8.11458C0.461806 7.19097 0 6.06944 0 4.75C0 3.43056 0.461806 2.30903 1.38542 1.38542C2.30903 0.461806 3.43056 0 4.75 0C5.60764 0 6.39931 0.21441 7.125 0.643229C7.85069 1.07205 8.43125 1.64931 8.86667 2.375H15.675L18.05 4.75L14.4875 8.075L12.825 6.8875L11.4 8.075L9.5 6.65H9.10417C8.7875 7.54722 8.22344 8.24653 7.41198 8.74792C6.60052 9.24931 5.71319 9.5 4.75 9.5ZM4.75 8.075C5.58125 8.075 6.31354 7.80781 6.94688 7.27344C7.58021 6.73906 7.94306 6.05625 8.03542 5.225H9.975L11.3604 6.25417L12.7656 5.08646L14.3687 6.23438L15.9917 4.73021L15.0813 3.8H7.93646C7.68576 3.10069 7.27344 2.53003 6.69948 2.08802C6.12552 1.64601 5.47569 1.425 4.75 1.425C3.82639 1.425 3.04132 1.74826 2.39479 2.39479C1.74826 3.04132 1.425 3.82639 1.425 4.75C1.425 5.67361 1.74826 6.45868 2.39479 7.10521C3.04132 7.75174 3.82639 8.075 4.75 8.075Z" fill="#9AA1AD"/>
</svg>}
                  iconRender={(visible) => (visible ? <EyeTwoTone twoToneColor="#A91D22" /> : <EyeInvisibleOutlined className="text-gray-500" />)}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Form.Item>

              <Form.Item label={<span className='text-sm font-medium text-gray-700 mb-0 block'>Confirm Password</span>} className='mb-0' layout='vertical'>
                <Input.Password
                  size="large"
                  style={{ backgroundColor: "#F8F9FA", borderColor: "#E5E7EB", height: 42 }}
                  className='light-password rounded-lg hover:border-gray-300 focus:border-[#A91D22]'
                  placeholder='Confirm new password'
                  prefix={<svg width="19" height="10" viewBox="0 0 19 10" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M4.75 6.65C4.22222 6.65 3.77361 6.46528 3.40417 6.09583C3.03472 5.72639 2.85 5.27778 2.85 4.75C2.85 4.22222 3.03472 3.77361 3.40417 3.40417C3.77361 3.03472 4.22222 2.85 4.75 2.85C5.27778 2.85 5.72639 3.03472 6.09583 3.40417C6.46528 3.77361 6.65 4.22222 6.65 4.75C6.65 5.27778 6.46528 5.72639 6.09583 6.09583C5.72639 6.46528 5.27778 6.65 4.75 6.65ZM4.75 9.5C3.43056 9.5 2.30903 9.03819 1.38542 8.11458C0.461806 7.19097 0 6.06944 0 4.75C0 3.43056 0.461806 2.30903 1.38542 1.38542C2.30903 0.461806 3.43056 0 4.75 0C5.60764 0 6.39931 0.21441 7.125 0.643229C7.85069 1.07205 8.43125 1.64931 8.86667 2.375H15.675L18.05 4.75L14.4875 8.075L12.825 6.8875L11.4 8.075L9.5 6.65H9.10417C8.7875 7.54722 8.22344 8.24653 7.41198 8.74792C6.60052 9.24931 5.71319 9.5 4.75 9.5ZM4.75 8.075C5.58125 8.075 6.31354 7.80781 6.94688 7.27344C7.58021 6.73906 7.94306 6.05625 8.03542 5.225H9.975L11.3604 6.25417L12.7656 5.08646L14.3687 6.23438L15.9917 4.73021L15.0813 3.8H7.93646C7.68576 3.10069 7.27344 2.53003 6.69948 2.08802C6.12552 1.64601 5.47569 1.425 4.75 1.425C3.82639 1.425 3.04132 1.74826 2.39479 2.39479C1.74826 3.04132 1.425 3.82639 1.425 4.75C1.425 5.67361 1.74826 6.45868 2.39479 7.10521C3.04132 7.75174 3.82639 8.075 4.75 8.075Z" fill="#9AA1AD"/>
</svg>}
                  iconRender={(visible) => (visible ? <EyeTwoTone twoToneColor="#A91D22" /> : <EyeInvisibleOutlined className="text-gray-500" />)}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </Form.Item>

              <Button type='primary' size='large' onClick={handleForgotPassword} loading={buttonLoading} className='w-full h-11 mt-4 text-base font-medium !bg-[#A91D22] hover:!bg-[#8a171b] !border-none rounded-lg shadow-md transition-all flex items-center justify-center'>
                Create New Password
              </Button>
            </div>

            <div className="flex items-center my-6">
              <div className="flex-grow border-t border-gray-200"></div>
              <span className="flex-shrink-0 mx-4 text-gray-400 text-[13px]">Back to Sign in?</span>
              <div className="flex-grow border-t border-gray-200"></div>
            </div>

            <Button 
              size='large' 
              onClick={() => navigate("/login")} 
              className='w-full h-11 text-base font-medium !text-[#A91D22] border !border-[#A91D22] !bg-[#fdf2f2] hover:!bg-[#fce4e4] rounded-lg transition-all flex items-center justify-center gap-2'
            >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M6.65 13.3V11.875H11.875V1.425H6.65V0H11.875C12.2669 0 12.6023 0.139532 12.8814 0.418594C13.1605 0.697657 13.3 1.03313 13.3 1.425V11.875C13.3 12.2669 13.1605 12.6023 12.8814 12.8814C12.6023 13.1605 12.2669 13.3 11.875 13.3H6.65ZM5.225 9.975L4.21562 8.96562L5.81875 7.3625H0V5.9375H5.81875L4.21562 4.33437L5.225 3.325L8.55 6.65L5.225 9.975Z" fill="#A51C1C"/>
</svg>
              Login to your account
            </Button>
          </div>
        </div>

        <span className='text-gray-400 text-xs md:text-sm select-none pb-6 mt-auto'>&copy; 2026 Vantage AI. All rights reserved.</span>
      </div>

      {/* Right side: Image Panel */}
      <div className='hidden lg:flex w-1/2 relative bg-black items-center justify-center overflow-hidden'>
        <img src={loginBackground} alt="Vantage AI Dashboard Graphic" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        
        <div className="absolute top-16 left-16 z-10">
          <div className="text-white text-[42px] leading-[1.2] font-bold tracking-tight">
            <div>Turn raw data into</div>
            <div>professional <span className="text-[#FCA5A5]">Dashboards</span></div>
            <div>in minutes.</div>
          </div>
        </div>

        {/* Valiantlytix Logo Overlay */}
        <div className="absolute bottom-12 right-12 z-10">
          <img src={valiantlytixLogo} alt="Valiantlytix Logo" className="w-[180px] h-auto object-contain" />
        </div>
      </div>

      {/* Error Modal */}
      <AuthErrorModal
        open={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        title={errorTitle}
        message={errorMessage || ""}
      />
    </div>
  )
}

export default ForgotPasswordPage